import {
  PATH_CUSTOMER_ACCOUNT_FAQ,
  PATH_DIY_RECIPES,
  PATH_GIFT,
  PATH_MEALS,
  PATH_PLAN,
  PATH_ORDERS,
  PATH_PROFILE,
  PATH_TREAT_PRODUCTS,
  PAW_PATH_MY_DOGS,
} from '@farmersdog/constants/paths';
import type { NavigationItem } from '@farmersdog/corgi-x';

import type { UseCustomerAccountData } from '@/account/app/hooks';
import {
  SubscriptionStatus,
  SubscriptionType,
} from '@/account/graphql/core/types';
import { getHomepagePath } from '@/account/routes/utils';

export interface AccountNavigation {
  /** All items on the left side of the top level nav **/
  items: NavigationItem[];
  /** All items on the left side of the top level nav **/
  menuItems: NavigationItem[];
  /** All items on the right side fo the top level nav **/
  rightItems?: NavigationItem[];
}

export interface GetAccountNavigationOptions {
  isMobile: boolean;
  showTreatsAndChews: boolean;
}

export function getAccountNavigation(
  data: UseCustomerAccountData,
  logout: () => void,
  options: GetAccountNavigationOptions
): AccountNavigation {
  const { isMobile, showTreatsAndChews } = options;

  const logoutItem: NavigationItem = {
    type: 'button',
    label: 'Log Out',
    onClick: e => {
      e.preventDefault();
      logout();
    },
  };

  const allItems = getAccountItems({ data, showTreatsAndChews });

  const isMobileFaqItem = (item: NavigationItem) =>
    isMobile && 'to' in item && item.to === PATH_CUSTOMER_ACCOUNT_FAQ;

  return {
    // on mobile views, hide the FAQ link
    items: allItems.filter(item => !isMobileFaqItem(item)),
    // mobile menu should also have the logout button
    menuItems: [...allItems, logoutItem],
    // on desktop, show the logout button on the right
    rightItems: [logoutItem],
  };
}

function getAccountItems({
  data,
  showTreatsAndChews,
}: {
  data: UseCustomerAccountData;
  showTreatsAndChews: boolean;
}): NavigationItem[] {
  const accountItems: NavigationItem[] = [];

  const homeItem = getHomeItem(data);
  if (homeItem) {
    accountItems.push(homeItem);
  }

  accountItems.push({
    type: 'link',
    label: 'Orders',
    to: PATH_ORDERS,
  });

  const planItem = getPlanItem(data);
  if (planItem) {
    accountItems.push(planItem);
  }

  const recipesItem = getRecipesItem(data);
  if (recipesItem) {
    accountItems.push(recipesItem);
  }

  const myDogsItem = getMyDogsItem(data);
  if (myDogsItem) {
    accountItems.push(myDogsItem);
  }

  const referralItem = getReferralItem(data);
  if (referralItem) {
    accountItems.push(referralItem);
  }

  const treatsItem = getTreatsItem(data, showTreatsAndChews);
  if (treatsItem) {
    accountItems.push(treatsItem);
  }

  accountItems.push(getFaqItem());

  const settingsItem = getSettingsItem(data);
  if (settingsItem) {
    accountItems.push(settingsItem);
  }

  return accountItems;
}

function getHomeItem(data: UseCustomerAccountData): NavigationItem | undefined {
  const homepagePath = getHomepagePath({
    subscriptionType: data.subscription?.type,
    isSubscriptionActive: data.isSubscriptionActive,
  });

  if (!homepagePath) {
    return;
  }

  return {
    type: 'link',
    label: 'Home',
    to: homepagePath,
  };
}

function getPlanItem(data: UseCustomerAccountData): NavigationItem | undefined {
  if (!data.activePets || data.activePets.length === 0) {
    return;
  }

  const isDiySubscription = data.subscription?.type === SubscriptionType.Diy;

  return isDiySubscription
    ? {
        type: 'link',
        label:
          data.activePets.length > 1
            ? `My Dogs' Plan`
            : `${data.activePets[0].name}'s Plan`,
        to: PATH_MEALS,
      }
    : {
        type: 'link',
        label: 'Plan',
        to: PATH_PLAN,
      };
}

function getRecipesItem(
  data: UseCustomerAccountData
): NavigationItem | undefined {
  const isDiySubscription = data.subscription?.type === SubscriptionType.Diy;
  if (!isDiySubscription) {
    return;
  }

  return {
    type: 'link',
    label: 'Recipes',
    to: PATH_DIY_RECIPES,
  };
}

function getMyDogsItem(
  data: UseCustomerAccountData
): NavigationItem | undefined {
  if (
    !data.isSubscriptionActive ||
    data.subscription?.type !== SubscriptionType.Fresh
  ) {
    return;
  }

  let label = 'My Dogs';

  if (data?.activePets?.length && data.activePets.length < 2) {
    label = 'My Dog';
  }

  return {
    type: 'link',
    label,
    to: PAW_PATH_MY_DOGS,
  };
}

function getReferralItem(
  data: UseCustomerAccountData
): NavigationItem | undefined {
  if (
    !data.subscription ||
    data.subscription.status !== SubscriptionStatus.Active ||
    data.subscription.type === SubscriptionType.Diy ||
    !data.referralStats ||
    !Number.isFinite(data.referralStats.discountPercentage)
  ) {
    return;
  }

  return {
    type: 'link',
    to: PATH_GIFT,
    label: 'Gift a Box',
  };
}

function getTreatsItem(
  data: UseCustomerAccountData,
  showTreatsAndChews: boolean
): NavigationItem | undefined {
  const isFresh = data.subscription?.type === SubscriptionType.Fresh;

  if (!isFresh) {
    return;
  }

  return {
    type: 'link',
    label: showTreatsAndChews ? 'Treats & Chews' : 'Treats',
    to: PATH_TREAT_PRODUCTS,
  };
}

function getFaqItem(): NavigationItem {
  return {
    type: 'link',
    to: PATH_CUSTOMER_ACCOUNT_FAQ,
    label: 'FAQ',
  };
}

function getSettingsItem(
  data: UseCustomerAccountData
): NavigationItem | undefined {
  if (
    !data.subscription ||
    data.subscription.status === SubscriptionStatus.Pending
  ) {
    return;
  }

  return {
    type: 'link',
    label: 'Settings',
    to: PATH_PROFILE,
  };
}
